import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Alert,
  Box,
  Typography,
  Checkbox,
  TextareaAutosize,
  Dialog as MuiDialog,
  DialogContentText,
  AlertTitle,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { addVehicle, getVehicleInfo } from "../../../../actions/formAction";
import Progress from "../../../../components/Loader/Progress ";
import { Formik, Form } from "formik";
import CustomDatePicker from "../../../../components/CustomDatePicker/CustomDatePicker";
import { baseUrl } from "../../../../util/constant";
import FileUpload from "../../../../components/FileUpload";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import * as Yup from "yup";

// Divider with colored bar and title
function DividerSP({ title  }) {
  return (
    <Box sx={{ pt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Box
          sx={{
            backgroundColor: "#d32f2f",
            width: 11,
            height: 24,
            borderRadius: 1,
          }}
        />
        <Typography variant="subtitle1" fontWeight="bold">
          {title}
        </Typography>
      </Box>
    </Box>
  );
}

// Checklist form for vehicle arrival
function ArrivalCheckList({ formik }) {
  const url = baseUrl + "";
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 2 }}>
      <CustomDatePicker
        label={"Arrival Date"}
        name="arrivalDate"
        disableFuture={true}
      />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {[
          { name: "title", label: "Title" },
          { name: "key", label: "Key" },
          { name: "partsWithCar", label: "Parts with Car" },
          { name: "removeEngine", label: "Remove Engine" },
          { name: "hasCatalyst", label: "Has Catalyst" },

        ].map((item) => (
          <Box
            key={item.name}
            sx={{ display: "flex", alignItems: "center", minWidth: 150 }}
          >
            <Checkbox
              onChange={formik.handleChange}
              checked={formik.values?.[item.name] || false}
              name={item.name}
              sx={{ "& .MuiSvgIcon-root": { fontSize: 24 } }}
            />
            <Typography>{item.label}</Typography>
          </Box>
        ))}
      </Box>
      <TextareaAutosize
        name="arrivalNote"
        style={{
          background: "#fff",
          minHeight: "90px",
          border: "1px solid #D9DCE1",
          borderRadius: "8px",
          padding: "10px",
          resize: "none",
          fontFamily: "inherit",
          fontSize: 15,
        }}
        placeholder={"Arrival Note (optional)"}
        value={formik.values.arrivalNote || ""}
        onChange={formik.handleChange}
      />
      <Box>
        <FileUpload
          disabled={false}
          label={"Warehouse Pictures"}
          uploadName="WarehousePictures"
          formikName="warehousePictures"
        />
        <Box sx={{ display: "flex", flexWrap: "wrap", pt: 2, gap: 2 }}>
          {formik.values?.warehousePictures?.map((item) => (
            <Box key={item.id}>
              <a
                href={`${url}${item.filePath}`}
                download={item.fileName}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <img
                  src={`${url}${item.filePath}`}
                  alt={item.fileName}
                  width={90}
                  height={90}
                  style={{ borderRadius: 6, border: "1px solid #eee" }}
                />
              </a>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function ArrivedModal({ isOpen, onClose, vehicleId   }) {
  const [initialValues, setInitialValues] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);
  const vehicleInfo = useSelector((state) => state.vehicleInfo);
  const add = useSelector((state) => state.add);

  const dispatch = useDispatch();

  useEffect(() => {
    if (isOpen) {
      dispatch(getVehicleInfo({ vehicleId }));
    }
  }, [dispatch, isOpen, vehicleId]);

  useEffect(() => {
    if (vehicleInfo.loading === "succeeded") {
      setInitialValues(vehicleInfo.Object);
      setInitialValues((prevValues) => ({
        ...prevValues,
        warehouseArrivalDate: new Date().toISOString().substr(0, 10), 
        status: { id: 2 }, // Assuming 2 is the ID for "Arrived at Warehouse"
      }));
    }
  }, [vehicleInfo]);

  const hasFormErrors = (formik) => {
    return Object.keys(formik.errors).length > 0;
  };

  // Validation schema for the form
  const validationSchema = Yup.object({
    arrivalDate: Yup.date().required("Arrival date is required"),
    title: Yup.boolean(),
    key: Yup.boolean(),
    partsWithCar: Yup.boolean(),
    removeEngine: Yup.boolean(),
    warehousePictures: Yup.array()
      .required("Warehouse pictures are required minimum 5 files")
      .min(5, "Minimum 5 pictures are required for warehouse arrival"),
  });

  // Simulate save logic
  const handleSave = async (values, actions) => {
    await dispatch(addVehicle({ data: values, id: vehicleId }));
    
  };

  useEffect(() => {
    if (add.vehicleAddedSuccessfully) {
      setShowConfirm(false);
      onClose();
    }

    if (add.hasError) {
      setShowConfirm(false);
    }
  }, [ add.hasError, onClose, add.vehicleAddedSuccessfully]);

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSave}
    >
      {(formik) => (
        <Form>
          <Dialog
            open={isOpen}
            onClose={add.isLoading ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={add.isLoading}
            disableBackdropClick={add.isLoading}
          >
            <DialogTitle
              sx={{ fontWeight: "bold", pr: 5, position: "relative" }}
            >
              Confirm Vehicle Arrival at Warehouse
              <Button
                aria-label="close"
                onClick={onClose}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                  minWidth: 0,
                  padding: 0,
                }}
              >
                <CloseIcon />
              </Button>
            </DialogTitle>
            <DialogContent>
              {vehicleInfo.loading === "pending" ||
              vehicleInfo.loading === "idle" ? (
                <Progress Msg="Loading vehicle information..." />
              ) : vehicleInfo.loading === "failed" ? (
                <Alert severity="error">
                  Error loading vehicle information.
                </Alert>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <Box
                    sx={{
                      background: "#f7fafd",
                      borderRadius: 2,
                      p: 2,
                      mb: 2,
                      boxShadow: "0 1px 4px #0001",
                    }}
                  >
                    <DividerSP title="Vehicle Information" />
                    <Typography variant="body1" sx={{ mb: 0.5 }}>
                      Client: {formik.values?.client?.name || "-"}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 0.5 }}>
                      Vehicle: {formik.values?.year} {formik.values?.make?.name}{" "}
                      {formik.values?.model?.name}{" "}
                      {formik.values?.vehicleType?.name}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 0 }}>
                      VIN: {formik.values?.vin || "-"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      background: "#fff",
                      borderRadius: 2,
                      p: 2,
                      boxShadow: "0 1px 4px #0001",
                    }}
                  >
                    <DividerSP title="Check List" />
                    <ArrivalCheckList formik={formik} />
                  </Box>
                </Box>
              )}
            </DialogContent>

            <DialogActions
              sx={{
                p: 2,
                flexDirection: "column-reverse",
                gap: 2,
                alignItems: "flex-end", // Align content to the right
              }}
            >
              {add.hasError && (
                <Box className="errorBox" sx={{ width: "100%" }}>
                  <Alert severity="error">
                    <AlertTitle>Error</AlertTitle>
                    {add.hasError ? <div>{add.errorMessage}</div> : ""}
                  </Alert>
                </Box>
              )}

              <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
                <Button
                  color="primary"
                  variant="b1"
                  startIcon={<SaveIcon />}
                  disabled={formik.isSubmitting}
                  onClick={async () => {
                    await formik.validateForm();
                    setShowConfirm(true);
                  }}
                >
                  Save
                </Button>
              </Box>
            </DialogActions>
          </Dialog>
          <MuiDialog
            open={showConfirm}
            onClose={add.isLoading ? undefined : () => setShowConfirm(false)}
            disableEscapeKeyDown={add.isLoading}
            disableBackdropClick={add.isLoading}
          >
            <DialogTitle>Save Confirmation</DialogTitle>
            <DialogContent>
              {hasFormErrors(formik) ? (
                <Box className="errorBox">
                  <Alert severity="error">
                    <AlertTitle>Error</AlertTitle>
                    {Object.keys(formik.errors).map((fieldName, index) => {
                      if (Array.isArray(formik?.errors[fieldName])) {
                      } else {
                        return (
                          <div key={index}>{formik.errors[fieldName]}</div>
                        );
                      }
                    })}
                  </Alert>
                </Box>
              ) : (
                <DialogContentText>
                  Are you sure you want to confirm the arrival of this vehicle
                  with the following information?
                  <br />
                  <strong>Client:</strong> {formik.values?.client?.name || "-"}
                  <br />
                  <strong>Vehicle:</strong> {formik.values?.year}{" "}
                  {formik.values?.make?.name} {formik.values?.model?.name}{" "}
                  {formik.values?.vehicleType?.name}
                  <br />
                  <strong>VIN:</strong> {formik.values?.vin || "-"}
                </DialogContentText>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowConfirm(false)} color="secondary">
                Cancel
              </Button>
              {!add.isLoading ? (
                <Button
                  onClick={() =>
                    hasFormErrors(formik)
                      ? setShowConfirm(false)
                      : formik.submitForm()
                  }
                  color="primary"
                  variant={hasFormErrors(formik) ? "b2" : "b1"}
                  startIcon={
                    hasFormErrors(formik) ? <CloseIcon /> : <SaveIcon />
                  }
                  disabled={formik.isSubmitting}
                >
                  {hasFormErrors(formik) ? "Close" : "Confirm Arrival"}
                </Button>
              ) : (
                <Progress Msg="Saving..." />
              )}
            </DialogActions>
          </MuiDialog>
        </Form>
      )}
    </Formik>
  );
}

ArrivedModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  vehicleId: PropTypes.string.isRequired,
};

export default ArrivedModal;